import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { founder } from '../../data/content';
import { ease } from '../../core/format/motion';
import type { Product, Routine } from '../../core/types/catalog';
type Section = {
  id: string;
  title: string;
  body: ReactNode;
};

/** Uitklapblokken onder de knop: waarom, gebruik, ingrediënten en huidtype. Eén tegelijk open. */
export function ProductDetails({
  product,
  inRoutines
}: {
  product: Product;
  inRoutines: Routine[];
}) {
  const [open, setOpen] = useState('why');
  const reduce = useReducedMotion();

  // Ander product: weer beginnen bij "waarom".
  useEffect(() => setOpen('why'), [product.id]);
  const sections: Section[] = [{
    id: 'why',
    title: `Waarom ${founder.firstName} dit kiest`,
    body: product.why
  }, {
    id: 'how',
    title: 'Zo gebruik je het',
    body: product.howTo
  }, {
    id: 'ingredients',
    title: 'Werkzame ingrediënten',
    body: <ul className="flex flex-wrap gap-2" data-id="element-8064" data-mp="xskk20tnkefg">
          {product.keyIngredients.map(i => <li key={i} className="rounded-full bg-blush px-3.5 py-1.5 text-[14px] text-ink" data-id="element-8065" data-mp="1bsd81odsogjq">
              {i}
            </li>)}
        </ul>
  }, {
    id: 'skin',
    title: 'Past bij',
    body: <div className="space-y-4" data-id="element-8066" data-mp="k0e3weqi27g0">
          <ul className="flex flex-wrap gap-2" data-id="element-8067" data-mp="k1fi2s2tba1y">
            {product.skinTypes.map(s => <li key={s} data-id="element-8068" data-mp="f3xkfxu8wh99">
                <Link to={`/webshop?huid=${encodeURIComponent(s)}`} className="inline-flex min-h-[36px] items-center rounded-full border border-ink/15 px-3.5 text-[14px] text-ink transition-colors duration-150 ease-soft hover:border-ink" data-id="element-8069" data-mp="sthg6jpese92">
                  {s}
                </Link>
              </li>)}
          </ul>
          {inRoutines.length > 0 && <p data-id="element-8070" data-mp="1vgwh2e4wqax9">
              Zit in{' '}
              {inRoutines.map((r, i) => <Fragment key={r.id} data-id="element-8071" data-mp="20f6aokazh3gr" data-mpi={i}>
                  {i > 0 && (i === inRoutines.length - 1 ? ' en ' : ', ')}
                  <Link to="/routines" className="text-ink underline underline-offset-4" data-id="element-8072" data-mp="255mr8da3p57c">
                    {r.name}
                  </Link>
                </Fragment>)}
              .
            </p>}
          <p data-id="element-8073" data-mp="1cisfq5v9hg60">Twijfel je? Stuur {founder.firstName} een appje met een foto — ze kijkt persoonlijk mee.</p>
        </div>
  }];
  return <div className="mt-8 divide-y divide-line border-y border-line" data-id="element-8074" data-mp="1mfoirw2e5a36">
      {sections.map(s => {
      const isOpen = open === s.id;
      const panelId = `product-${s.id}`;
      return <div key={s.id} data-id="element-8075" data-mp="2xutwdv428t1k">
            <h2 data-id="element-8076" data-mp="2xvmwid8ikkaq">
              <button type="button" aria-expanded={isOpen} aria-controls={panelId} onClick={() => setOpen(isOpen ? '' : s.id)} className="flex w-full items-center justify-between py-4 text-left text-[15px] text-ink" data-id="element-8077" data-mp="2xu20ej6jwtar">
                {s.title}
                <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-200 ease-soft ${isOpen ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-8078" data-mp="m9uhn19hyxa1" />
              </button>
            </h2>
            <AnimatePresence initial={false} data-id="element-8079" data-mp="7ghi9tl0liop">
              {isOpen && <motion.div id={panelId} initial={reduce ? {
            opacity: 0
          } : {
            height: 0,
            opacity: 0
          }} animate={reduce ? {
            opacity: 1
          } : {
            height: 'auto',
            opacity: 1
          }} exit={reduce ? {
            opacity: 0
          } : {
            height: 0,
            opacity: 0
          }} transition={{
            duration: 0.22,
            ease
          }} className="overflow-hidden" data-id="element-8080" data-mp="3g7fepsir8e2">
                  <div className="pb-5 text-[15px] leading-relaxed text-muted" data-id="element-8081" data-mp="1nur70ot659g2">{s.body}</div>
                </motion.div>}
            </AnimatePresence>
          </div>;
    })}
    </div>;
}